import React, { useState, useEffect } from 'react';
import {
  getPurchaseOrderById,
  PurchaseOrder,
  PurchaseOrderStatus,
  submitPurchaseOrder,
  approvePurchaseOrder,
  cancelPurchaseOrder,
} from '../../services/purchaseOrderService';
import Loader from '../../components/common/Loader';
import ErrorMessage from '../../components/common/ErrorMessage';
import ReceivePOModal from './ReceivePOModal';

interface PurchaseOrderDetailsProps {
  poId: number;
  onClose: () => void;
  onUpdate: () => void;
}

const PurchaseOrderDetails: React.FC<PurchaseOrderDetailsProps> = ({ poId, onClose, onUpdate }) => {
  const [po, setPO] = useState<PurchaseOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showReceiveModal, setShowReceiveModal] = useState(false);

  const loadPODetails = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getPurchaseOrderById(poId);
      setPO(data);
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Failed to load PO details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPODetails();
  }, [poId]);

  const handleSubmit = async () => {
    if (!po) return;
    
    if (!window.confirm('Send this PO to supplier?')) return;

    try {
      setActionLoading(true);
      await submitPurchaseOrder(po.id);
      await loadPODetails();
      onUpdate();
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Failed to send PO');
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!po) return;
    
    if (!window.confirm('Approve this PO?')) return;

    try {
      setActionLoading(true);
      await approvePurchaseOrder(po.id);
      await loadPODetails();
      onUpdate();
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Failed to approve PO');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!po) return;
    
    const reason = window.prompt('Enter cancellation reason:');
    if (!reason) return;

    try {
      setActionLoading(true);
      await cancelPurchaseOrder(po.id, reason);
      await loadPODetails();
      onUpdate();
    } catch (err: any) {
      alert(err.response?.data?.detail || err.message || 'Failed to cancel PO');
    } finally {
      setActionLoading(false);
    }
  };

  const handleReceiveSuccess = async () => {
    await loadPODetails();
    onUpdate();
  };

  const getStatusBadge = (status: PurchaseOrderStatus) => {
    const styles = {
      [PurchaseOrderStatus.DRAFT]: 'bg-gray-100 text-gray-800',
      [PurchaseOrderStatus.SENT]: 'bg-blue-100 text-blue-800',
      [PurchaseOrderStatus.PARTIAL]: 'bg-yellow-100 text-yellow-800',
      [PurchaseOrderStatus.COMPLETED]: 'bg-green-100 text-green-800',
      [PurchaseOrderStatus.CANCELLED]: 'bg-red-100 text-red-800',
    };

    const labels = {
      [PurchaseOrderStatus.DRAFT]: 'Draft',
      [PurchaseOrderStatus.SENT]: 'Sent',
      [PurchaseOrderStatus.PARTIAL]: 'Partial',
      [PurchaseOrderStatus.COMPLETED]: 'Completed',
      [PurchaseOrderStatus.CANCELLED]: 'Cancelled',
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${styles[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const canSubmit = po?.status === PurchaseOrderStatus.DRAFT;
  const canApprove = false; // No approval needed in backend
  const canReceive = po && (po.status === PurchaseOrderStatus.SENT || po.status === PurchaseOrderStatus.PARTIAL);
  const canCancel = po && ![PurchaseOrderStatus.COMPLETED, PurchaseOrderStatus.CANCELLED].includes(po.status);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <Loader />
        </div>
      </div>
    );
  }

  if (error || !po) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <ErrorMessage message={error || 'PO not found'} onRetry={loadPODetails} />
          <button
            onClick={onClose}
            className="mt-4 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  const totalOrdered = po.items.reduce((sum, item) => sum + item.qty_ordered, 0);
  const totalReceived = po.items.reduce((sum, item) => sum + item.qty_received, 0);
  const hasPendingItems = po.items.some(item => item.qty_received < item.qty_ordered);

  return (
    <>
      {showReceiveModal && (
        <ReceivePOModal
          po={po}
          onClose={() => setShowReceiveModal(false)}
          onSuccess={handleReceiveSuccess}
        />
      )}

      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl mx-4 my-8 max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="p-6 border-b bg-gradient-to-r from-blue-600 to-blue-700 text-white">
            <div className="flex justify-between items-start">
              <div>
                <h2 className="text-3xl font-bold mb-2">Purchase Order Details</h2>
                <p className="text-xl opacity-90">{po.po_number}</p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1">
            {/* Status and Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Order Information</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span>{getStatusBadge(po.status)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Order Date:</span>
                    <span className="font-medium">{formatDate(po.order_date)}</span>
                  </div>
                  {po.expected_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Expected Date:</span>
                      <span className="font-medium">{formatDate(po.expected_date)}</span>
                    </div>
                  )}
                  {po.received_date && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Received Date:</span>
                      <span className="font-medium">{formatDate(po.received_date)}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="text-lg font-semibold mb-3 text-gray-800">Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Items:</span>
                    <span className="font-medium">{po.items.length}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Ordered:</span>
                    <span className="font-medium">{totalOrdered} units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total Received:</span>
                    <span className="font-medium text-green-600">{totalReceived} units</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Pending:</span>
                    <span className="font-medium text-orange-600">{totalOrdered - totalReceived} units</span>
                  </div>
                  <div className="flex justify-between pt-2 border-t">
                    <span className="text-gray-600">Total Amount:</span>
                    <span className="font-bold text-lg text-blue-600">{formatCurrency(po.total_amount)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Notes */}
            {po.notes && (
              <div className="mb-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-2">Notes:</h3>
                <p className="text-sm text-gray-700">{po.notes}</p>
              </div>
            )}

            {/* Items Table */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold mb-3 text-gray-800">Items</h3>
              <div className="bg-white rounded-lg border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">SKU</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty Ordered</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Qty Received</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Pending</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Unit Price</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Subtotal</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {po.items.map((item) => {
                        const pending = item.qty_ordered - item.qty_received;
                        const isFullyReceived = item.qty_received >= item.qty_ordered;

                        return (
                          <tr key={item.id} className={isFullyReceived ? 'bg-green-50' : ''}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">
                              {item.item_name}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-600">{item.item_sku}</td>
                            <td className="px-4 py-3 text-sm text-right font-medium">{item.qty_ordered}</td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={item.qty_received > 0 ? 'text-green-600 font-medium' : 'text-gray-400'}>
                                {item.qty_received}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right">
                              <span className={pending > 0 ? 'text-orange-600 font-medium' : 'text-gray-400'}>
                                {pending}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-sm text-right">{formatCurrency(item.unit_price)}</td>
                            <td className="px-4 py-3 text-sm text-right font-semibold">
                              {formatCurrency(item.subtotal)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          {/* Footer Actions */}
          <div className="p-6 border-t bg-gray-50 flex justify-between items-center">
            <button
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-100"
            >
              Close
            </button>

            <div className="flex gap-3">
              {canCancel && (
                <button
                  onClick={handleCancel}
                  disabled={actionLoading}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  Cancel PO
                </button>
              )}

              {canSubmit && (
                <button
                  onClick={handleSubmit}
                  disabled={actionLoading}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  Send to Supplier
                </button>
              )}

              {canReceive && hasPendingItems && (
                <button
                  onClick={() => setShowReceiveModal(true)}
                  disabled={actionLoading}
                  className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                  </svg>
                  Receive Items
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PurchaseOrderDetails;
